/**
 * 事件总线
 * 职责：统一管理事件的分发、拦截、订阅
 */

export interface TrackEvent {
  event_id: number;
  event_name: string;
  data: Record<string, any>;
  timestamp: number;
  trace_id?: string; // 兼容测试和旧代码
  metadata?: {
    source: "v2" | "legacy";
    sessionId?: string;
    traceId?: string;
  };
}

export type EventInterceptor = (event: TrackEvent) => TrackEvent | null;
export type EventListener = (event: TrackEvent) => void;

export class EventBus {
  private interceptors: EventInterceptor[] = [];
  private subscribers: Map<string, Set<EventListener>> = new Map();
  private eventHistory: TrackEvent[] = [];
  private maxHistorySize = 100;

  /**
   * 添加事件拦截器
   * 拦截器可以修改事件或阻止事件发送（返回 null）
   */
  addInterceptor(interceptor: EventInterceptor): () => void {
    this.interceptors.push(interceptor);

    if (process.env.NODE_ENV === "development") {
      console.log("[EventBus] Added interceptor");
    }

    // 返回取消拦截器函数
    return () => {
      const index = this.interceptors.indexOf(interceptor);
      if (index > -1) {
        this.interceptors.splice(index, 1);
      }
    };
  }

  /**
   * 订阅事件
   */
  on(eventName: string, handler: EventListener): () => void {
    if (!this.subscribers.has(eventName)) {
      this.subscribers.set(eventName, new Set());
    }
    this.subscribers.get(eventName)!.add(handler);

    if (process.env.NODE_ENV === "development") {
      console.log(`[EventBus] Subscribed to event: ${eventName}`);
    }

    // 返回取消订阅函数
    return () => {
      this.subscribers.get(eventName)?.delete(handler);
    };
  }

  /**
   * 发送事件
   */
  emit(event: TrackEvent): void {
    // 1. 执行拦截器
    let processedEvent: TrackEvent | null = event;
    for (const interceptor of this.interceptors) {
      try {
        processedEvent = interceptor(processedEvent);
        if (processedEvent === null) {
          // 拦截器返回 null，表示丢弃事件
          if (process.env.NODE_ENV === "development") {
            console.log(
              `[EventBus] Event intercepted and discarded: ${event.event_name}`
            );
          }
          return;
        }
      } catch (error) {
        console.error("[EventBus] Interceptor error:", error);
      }
    }

    // 2. 记录事件历史
    this.eventHistory.push(processedEvent);
    if (this.eventHistory.length > this.maxHistorySize) {
      this.eventHistory.shift();
    }

    // 3. 通知订阅者
    const handlers = this.subscribers.get(processedEvent.event_name);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(processedEvent);
        } catch (error) {
          console.error(
            `[EventBus] Handler error for "${processedEvent.event_name}":`,
            error
          );
        }
      });
    }

    // 4. 开发环境日志
    if (process.env.NODE_ENV === "development") {
      console.log(
        `[EventBus] Event emitted: ${processedEvent.event_name}`,
        processedEvent
      );
    }
  }

  /**
   * 获取事件历史
   */
  getHistory(): TrackEvent[] {
    return [...this.eventHistory];
  }

  /**
   * 回放事件（用于调试）
   */
  replay(handler: EventListener): void {
    this.eventHistory.forEach(handler);

    if (process.env.NODE_ENV === "development") {
      console.log(`[EventBus] Replayed ${this.eventHistory.length} events`);
    }
  }

  /**
   * 清空事件历史
   */
  clearHistory(): void {
    this.eventHistory = [];

    if (process.env.NODE_ENV === "development") {
      console.log("[EventBus] Event history cleared");
    }
  }

  /**
   * 清除所有订阅者
   */
  clearSubscribers(): void {
    this.subscribers.clear();

    if (process.env.NODE_ENV === "development") {
      console.log("[EventBus] All subscribers cleared");
    }
  }

  /**
   * 清除所有拦截器
   */
  clearInterceptors(): void {
    this.interceptors = [];

    if (process.env.NODE_ENV === "development") {
      console.log("[EventBus] All interceptors cleared");
    }
  }
}

// 导出单例
export const eventBus = new EventBus();
